import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Calendar as CalIcon, Film, MapPin, Play, Plus, Pencil, Trash2, X } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import CopyInviteButton from "@/components/CopyInviteButton";
import UpgradeModal from "@/components/UpgradeModal";

function EditEventDialog({ open, event, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState("19:00");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && event) {
      setTitle(event.title || "");
      setDesc(event.description || "");
      setLocation(event.location || "");
      const dt = event.starts_at ? new Date(event.starts_at) : new Date();
      setDate(dt);
      const hh = String(dt.getHours()).padStart(2, "0");
      const mm = String(dt.getMinutes()).padStart(2, "0");
      setTime(`${hh}:${mm}`);
    }
  }, [open, event]);

  if (!open || !event) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !date) return;
    setBusy(true);
    try {
      const [h, m] = time.split(":").map(Number);
      const dt = new Date(date);
      dt.setHours(h, m, 0, 0);
      const { data } = await api.patch(`/events/${event.id}`, {
        title, description: desc, starts_at: dt.toISOString(), location,
      });
      onSaved(data);
      toast.success("Gathering updated");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-jjblue/60 backdrop-blur-sm" data-testid="event-edit-dialog">
      <div className="jj-card w-full max-w-3xl max-h-[90vh] overflow-y-auto p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--jj-muted)] hover:text-burgundy"><X className="h-5 w-5" /></button>
        <div className="overline text-burgundy">Edit gathering</div>
        <h2 className="font-serif text-2xl text-jjblue mt-2 leading-tight">Update the details</h2>
        <form onSubmit={submit} className="mt-5 grid md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-jjblue/80">Title</label>
            <input className="input-jj mt-1" required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="event-edit-title" />
            <label className="text-sm text-jjblue/80 mt-4 block">Description</label>
            <textarea rows={3} className="input-jj mt-1" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="event-edit-desc" />
            <label className="text-sm text-jjblue/80 mt-4 block">Location</label>
            <input className="input-jj mt-1" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Online / address" data-testid="event-edit-location" />
            <label className="text-sm text-jjblue/80 mt-4 block">Time</label>
            <input type="time" className="input-jj mt-1 max-w-[160px]" value={time} onChange={(e) => setTime(e.target.value)} data-testid="event-edit-time" />
          </div>
          <div>
            <label className="text-sm text-jjblue/80">Date</label>
            <div className="mt-1 jj-card p-2 inline-block">
              <Calendar mode="single" selected={date} onSelect={setDate} />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
              <button disabled={busy} className="btn-primary !py-2 !px-4 text-sm" data-testid="event-edit-submit">{busy ? "Saving…" : "Save changes"}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Events() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState("19:00");
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const [upgrade, setUpgrade] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/events");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !date) return;
    setBusy(true);
    try {
      const [h, m] = time.split(":").map(Number);
      const dt = new Date(date);
      dt.setHours(h, m, 0, 0);
      await api.post("/events", { title, description: desc, starts_at: dt.toISOString(), location });
      setTitle(""); setDesc(""); setLocation(""); setOpen(false);
      load();
      toast.success("Gathering scheduled");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403 && typeof detail === "string") {
        setOpen(false);
        setUpgrade({ tier: "Disciple", reason: detail });
      } else {
        toast.error("Could not schedule");
      }
    } finally { setBusy(false); }
  };

  const rsvp = async (e) => {
    const { data } = await api.post(`/events/${e.id}/rsvp`);
    setItems(items.map((x) => x.id === e.id ? { ...x, ...data } : x));
  };

  const removeEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    try {
      await api.delete(`/events/${ev.id}`);
      setItems((arr) => arr.filter((x) => x.id !== ev.id));
      toast.success("Gathering deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-burgundy">Gatherings & Prayer Nights</div>
          <h1 className="font-serif text-4xl text-jjblue mt-2">Mark the holy moments</h1>
          <p className="mt-2 text-[var(--jj-text-secondary)]">Prayer nights, fasts, worship sessions—keep them on a shared calendar.</p>
        </div>
        <button onClick={() => setOpen(!open)} className="btn-primary flex items-center gap-2" data-testid="new-event-btn">
          <Plus className="h-4 w-4" /> New gathering
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="jj-card p-6 mt-6 grid md:grid-cols-2 gap-6" data-testid="new-event-form">
          <div>
            <label className="text-sm text-jjblue/80">Title</label>
            <input className="input-jj mt-1" required value={title} onChange={(e) => setTitle(e.target.value)} data-testid="e-title" />
            <label className="text-sm text-jjblue/80 mt-4 block">Description</label>
            <textarea rows={3} className="input-jj mt-1" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="e-desc" />
            <label className="text-sm text-jjblue/80 mt-4 block">Location</label>
            <input className="input-jj mt-1" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Online / address" data-testid="e-location" />
            <label className="text-sm text-jjblue/80 mt-4 block">Time</label>
            <input type="time" className="input-jj mt-1 max-w-[160px]" value={time} onChange={(e) => setTime(e.target.value)} data-testid="e-time" />
          </div>
          <div>
            <label className="text-sm text-jjblue/80">Date</label>
            <div className="mt-1 jj-card p-2 inline-block">
              <Calendar mode="single" selected={date} onSelect={setDate} data-testid="e-date" />
            </div>
            <div className="mt-4 flex justify-end">
              <button disabled={busy} className="btn-primary !py-2 !px-4 text-sm" data-testid="e-submit">{busy ? "Scheduling…" : "Schedule"}</button>
            </div>
          </div>
        </form>
      )}

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.length === 0 && <div className="text-center py-10 text-[var(--jj-muted)] col-span-full">No gatherings yet. Schedule one to gather the family.</div>}
        {items.map((e) => {
          const isWatchParty = Boolean(e.movie_id);
          const canManage = user && (user.role === "admin" || e.created_by === user.id);
          return (
          <div key={e.id} className={`jj-card overflow-hidden flex flex-col ${isWatchParty ? "ring-1 ring-gold/40" : ""}`} data-testid={`event-${e.id}`}>
            {isWatchParty && e.poster_url && (
              <Link to={`/cinema/${e.movie_id}`} className="relative block aspect-[16/8] overflow-hidden bg-jjblue group" data-testid={`event-poster-${e.id}`}>
                <img src={e.poster_url} alt="" loading="lazy" className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-jjblue/85 via-jjblue/20 to-transparent" />
                <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-gold text-jjblue text-[10px] font-semibold tracking-wide px-2 py-1 rounded-full uppercase">
                  <Film className="h-3 w-3" /> Watch Party
                </span>
                <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 bg-cream/90 text-burgundy text-[11px] font-medium px-2.5 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <Play className="h-3 w-3 fill-current" /> Open in Cinema
                </span>
              </Link>
            )}
            <div className="p-6 flex-1 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 text-burgundy text-xs"><CalIcon className="h-4 w-4" /> {new Date(e.starts_at).toLocaleString()}</div>
                {canManage && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditing(e)} className="p-1 text-[var(--jj-muted)] hover:text-burgundy" title="Edit" data-testid={`event-edit-${e.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => removeEvent(e)} className="p-1 text-[var(--jj-muted)] hover:text-burgundy" title="Delete" data-testid={`event-delete-${e.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              <h3 className="font-serif text-2xl text-jjblue mt-2 leading-tight">{e.title}</h3>
              <p className="text-sm text-[var(--jj-text-secondary)] mt-2 line-clamp-3 min-h-[3.5rem]">{e.description}</p>
              {e.location && <div className="mt-3 text-xs text-[var(--jj-muted)] flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {e.location}</div>}
              <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
                <span className="text-xs text-[var(--jj-muted)]">{e.rsvp_count} attending</span>
                <div className="flex items-center gap-2 flex-wrap">
                  {isWatchParty && (
                    <>
                      <CopyInviteButton
                        url={`${window.location.origin}/api/og/cinema/${e.movie_id}?party=${e.id}${user?.id ? `&inviter=${user.id}` : ""}`}
                        label="Invite"
                        copiedLabel="Copied ✓"
                        testId={`event-invite-${e.id}`}
                      />
                      <Link to={`/cinema/${e.movie_id}`} className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1" data-testid={`event-watch-${e.id}`}>
                        <Play className="h-3 w-3 fill-current" /> Watch
                      </Link>
                    </>
                  )}
                  <button onClick={() => rsvp(e)} className={e.i_rsvp ? "btn-secondary !py-1.5 !px-3 text-xs" : "btn-gold !py-1.5 !px-3 text-xs"} data-testid={`rsvp-${e.id}`}>
                    {e.i_rsvp ? "Going" : "RSVP"}
                  </button>
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      <UpgradeModal
        open={!!upgrade}
        onClose={() => setUpgrade(null)}
        tier={upgrade?.tier}
        reason={upgrade?.reason}
      />
      <EditEventDialog
        open={!!editing}
        event={editing}
        onClose={() => setEditing(null)}
        onSaved={(updated) => setItems((arr) => arr.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))}
      />
    </div>
  );
}
